import React, { useCallback, useRef, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Box, Typography, IconButton } from "@mui/material";
import AddAPhotoIcon from "@mui/icons-material/AddAPhoto";
import { createStyleSheet } from "../utils/styles";
import { theme } from "../../theme";
import type { FileInputProps } from "../types/projects";

export const FileInput = (props: FileInputProps) => {
  const { value, onFileInputChange } = props;
  const [preview, setPreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { t } = useTranslation("FileInput");

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0] || null;
      onFileInputChange(file);
    },
    [onFileInputChange]
  );

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  useEffect(() => {
    if (!value) {
      setPreview(null);
      return;
    }
    let objectUrl: string | null = null;
    if (value instanceof Blob) {
      objectUrl = URL.createObjectURL(value);
      setPreview(objectUrl);
    } else if (typeof value === "string") {
      setPreview(value);
    }
    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [value]);

  return (
    <Box sx={styles.container}>
      <Box sx={styles.imageBox} onClick={triggerFileInput}>
        <input
          type="file"
          accept="image/jpeg"
          onChange={handleFileChange}
          ref={fileInputRef}
          aria-label="File input"
          style={{ display: "none" }}
          data-testid="file-input"
        />
        {preview ? (
          <Box component="img" src={preview} alt="Uploaded" sx={styles.previewImage} />
        ) : (
          <IconButton sx={styles.iconButton} component="span">
            <AddAPhotoIcon fontSize="large" />
          </IconButton>
        )}
      </Box>
      <Typography sx={styles.label}>{preview ? t("editTestImg") : t("addTestImg")}</Typography>
    </Box>
  );
};

const styles = createStyleSheet({
  container: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  imageBox: {
    width: 100,
    height: 100,
    backgroundColor: theme.palette.imageBackground.main,
    borderRadius: "10px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    overflow: "hidden",
    cursor: "pointer",
  },
  previewImage: {
    width: "100%",
    height: "100%",
    objectFit: "contain",
    borderRadius: "16px",
  },
  iconButton: {
    color: theme.palette.secondaryBackground.main,
    width: "100%",
    height: "100%",
  },
  label: {
    marginTop: 1,
    textAlign: "center",
    fontSize: "14px",
    fontWeight: 500,
  },
});
